import React, { useState, useContext } from "react";
import { Category } from "../data/RowData";

interface ContextState {
  search: string;
  tabIndex: number | null;
  category: Category;
  hamburgerMenuIsOpen: boolean;
  isActive: boolean;
  errors: string;
}

interface ContextType {
  state: ContextState;
  setState: <K extends keyof ContextState>(
    key: K,
    value: ContextState[K]
  ) => void;
  resetAll: () => void;
}

const defaultState: ContextState = {
  search: "",
  tabIndex: null,
  category: Category.all,
  hamburgerMenuIsOpen: false,
  isActive: false,
  errors: "",
};

const AppContext = React.createContext<ContextType>({
  state: defaultState,
  setState: () => {},
  resetAll: () => {},
});

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setInternalState] = useState<ContextState>(defaultState);

  const setState = <K extends keyof ContextState>(
    key: K,
    value: ContextState[K]
  ) => {
    setInternalState((prevState) => ({ ...prevState, [key]: value }));
  };

  const resetAll = () => {
    setInternalState(defaultState);
  };

  return (
    <AppContext.Provider value={{ state, setState, resetAll }}>
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

export { AppProvider, useAppContext };
